import useStyles from 'isomorphic-style-loader/useStyles';
import type { PropsWithChildren, ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import type { PopupProps } from 'semantic-ui-react';
import { Popup } from 'semantic-ui-react';
import { useResizeObserver } from 'usehooks-ts';

import s from './truncate-text.scss';

export type TruncateTextProps = PropsWithChildren<
  Omit<PopupProps, 'content' | 'trigger'>
> & {
  renderContent?: (content: ReactNode) => ReactNode;
};

/**
 * Wraps content in a Popup containing the full text, if the content is too long
 * to fit in its container. Text content is truncated with an ellipsis when its
 * inline length exceeds the max-width of its parent container. When the content
 * does not need to be truncated, the Popup is disabled.
 */
export const TruncateText = ({
  children,
  disabled,
  position = 'top center',
  mouseEnterDelay = 250,
  renderContent = content => content,
  ...popupProps
}: TruncateTextProps) => {
  useStyles(s);

  const ref = useRef<HTMLSpanElement>(null);

  const { width } = useResizeObserver({ ref });

  const isOverflowing = useMemo(() => {
    if (!ref.current) return false;

    const { scrollWidth, clientWidth } = ref.current;

    // Fallback to clientWidth when width is sometimes 0
    return scrollWidth > (width || clientWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Needs to react to clientWidth changes
  }, [width, ref.current?.clientWidth]);

  const trigger = (
    <span ref={ref} className={s.trigger}>
      {children}
    </span>
  );

  return (
    <Popup
      role="tooltip"
      content={renderContent(children)}
      disabled={disabled ?? !isOverflowing}
      mouseEnterDelay={mouseEnterDelay}
      position={position}
      trigger={trigger}
      {...popupProps}
    />
  );
};

export default TruncateText;
